// /markt/japan-carry — eigene Page für Japan-Carry-Trade-Monitor.
// Doc-Anweisung 2026-05-17: aus dem Right-Rail-Card-Skelett wird ein
// eigener Untertab unter MARKT analog Polymarket/Trending.

import { useEffect } from 'react';
import { AlertCircle, Sparkles, TrendingDown, TrendingUp } from 'lucide-react';
import { selectFullData, useMarketStore } from '@/stores/market-store';
import { useEvePageContextStore } from '@/stores/eve-page-context-store';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';

function fmt(v: number | null | undefined, opts: { digits?: number; suffix?: string } = {}): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const formatted = v.toLocaleString('de-DE', {
    maximumFractionDigits: opts.digits ?? 2,
    minimumFractionDigits: opts.digits ?? 2,
  });
  return opts.suffix ? `${formatted}${opts.suffix}` : formatted;
}

export function JapanCarryPage() {
  const fullData = useMarketStore(selectFullData);
  const setPageContext = useEvePageContextStore((s) => s.setPageContext);
  const clearPageContext = useEvePageContextStore((s) => s.clearPageContext);
  const askEve = useEveDrawerStore((s) => s.askEve);

  const j = fullData?.japan;
  const usdjpy = j?.usdjpy?.value ?? null;
  const jp10y = j?.jp10y?.value ?? null;
  const carry = j?.bondSpread ?? null;
  const policy = j?.policySpread ?? null;
  const meetings = j?.bojMeetings ?? [];

  // Carry-Risiko-Bucket
  const risk = carry === null ? null : carry > 3.5 ? 'HOCH' : carry > 2.5 ? 'MITTEL' : 'NIEDRIG';
  const riskTone =
    risk === 'HOCH'
      ? 'border-rose-500/40 bg-rose-500/15 text-rose-300'
      : risk === 'MITTEL'
        ? 'border-amber-500/40 bg-amber-500/15 text-amber-300'
        : 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300';

  useEffect(() => {
    setPageContext({
      pageKey: '/markt/japan-carry',
      title: 'Japan / Carry-Trade',
      summary: `USD/JPY ${fmt(usdjpy)} · JP10Y ${jp10y !== null ? fmt(jp10y, { digits: 3, suffix: '%' }) : '—'} · Carry-Spread ${carry !== null ? fmt(carry, { suffix: '%' }) : '—'} · Risiko ${risk ?? '—'}`,
      data: { usdjpy, jp10y, carry, policy, risk, meetings },
    });
    return () => clearPageContext();
  }, [usdjpy, jp10y, carry, policy, risk, meetings, setPageContext, clearPageContext]);

  return (
    <div className="h-full overflow-y-auto p-5">
      <div className="mx-auto max-w-5xl space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🇯🇵</span>
              <h1 className="text-xl font-bold">Japan · Carry-Trade-Monitor</h1>
            </div>
            <p className="mt-1 text-[12px] text-neutral-500">
              USD/JPY · JP10Y · Bond-Spread · Policy-Spread · BOJ-Meetings. Frühwarnung für Yen-Carry-Unwinds.
            </p>
          </div>
          {risk ? (
            <span
              className={`rounded-full border px-3 py-1 text-[12px] font-bold uppercase tracking-wider ${riskTone}`}
            >
              Carry-Risiko: {risk}
            </span>
          ) : null}
        </header>

        {/* KPI-Grid */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <KpiTile
            label="USD/JPY"
            value={fmt(usdjpy, { digits: 2 })}
            sub={j?.usdjpy?.date ?? '—'}
            tone="neutral"
          />
          <KpiTile
            label="JP 10Y"
            value={jp10y !== null ? fmt(jp10y, { digits: 3, suffix: ' %' }) : '—'}
            sub={j?.jp10y?.date ?? 'noch nicht geladen'}
            tone="neutral"
          />
          <KpiTile
            label="Bond-Spread (US10Y − JP10Y)"
            value={carry !== null ? fmt(carry, { suffix: ' %' }) : '—'}
            sub={
              risk === 'HOCH'
                ? 'Unwind-Risiko aktiv'
                : risk === 'MITTEL'
                  ? 'Spread expansiv'
                  : 'Spread komprimiert'
            }
            tone={risk === 'HOCH' ? 'rose' : risk === 'MITTEL' ? 'amber' : 'emerald'}
          />
          <KpiTile
            label="Policy-Spread (Fed − BOJ)"
            value={policy !== null ? fmt(policy, { suffix: ' %' }) : '—'}
            sub="Strukturelle Yen-Schwäche-Treiber"
            tone="neutral"
          />
        </div>

        {/* BOJ-Meetings */}
        <section className="glass-strong rounded-2xl p-4">
          <h2 className="mb-3 text-[13px] font-bold uppercase tracking-wider text-neutral-400">
            Nächste BOJ-Meetings · Hike-Wahrscheinlichkeit
          </h2>
          {meetings.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 p-4 text-center text-[12px] text-neutral-500">
              BOJ-Meetings noch nicht geladen — kommen täglich 06:00 UTC vom FRED-Cron.
            </div>
          ) : (
            <ul className="space-y-2">
              {meetings.slice(0, 6).map((mt) => {
                const tone =
                  mt.hikeProb > 50
                    ? 'text-rose-300 border-rose-500/30 bg-rose-500/10'
                    : mt.hikeProb > 25
                      ? 'text-amber-300 border-amber-500/30 bg-amber-500/10'
                      : 'text-neutral-300 border-white/10 bg-white/[0.03]';
                return (
                  <li
                    key={mt.date}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 ${tone}`}
                  >
                    <span className="text-[13px] font-medium">{mt.label}</span>
                    <span className="text-[14px] font-bold tabular">{mt.hikeProb.toFixed(1)} %</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Erklärungs-Box */}
        <section className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-4">
          <div className="mb-2 flex items-center gap-2">
            <AlertCircle className="size-4 text-amber-400" />
            <h2 className="text-[12px] font-bold uppercase tracking-wider text-amber-300">
              Warum Japan-Carry beobachten?
            </h2>
          </div>
          <ul className="space-y-1 text-[12px] leading-relaxed text-neutral-300">
            <li>
              • Carry-Trader leihen JPY zu niedrigen Sätzen, kaufen USD-Bonds — Spread &gt; 2.5 % macht
              die Position attraktiv, &gt; 3.5 % heißt extreme Positionierung mit Unwind-Risiko.
            </li>
            <li>
              • BOJ-Hike oder unerwartete Yen-Stärke triggert Unwind → globale Risk-Off-Welle (Aug 2024
              war das Paradebeispiel: VIX-Spike + Tech-Crash).
            </li>
            <li>
              • Policy-Spread (Fed − BOJ) = strukturelle Schwäche-Lage. Fällt der Spread, kippt der Carry.
            </li>
          </ul>
        </section>

        {/* Frag-EVE-CTA */}
        <button
          type="button"
          onClick={() =>
            askEve(
              'Schau dir die aktuelle Japan-Carry-Lage an: USD/JPY, JP10Y, Bond-Spread, BOJ-Hike-Wahrscheinlichkeiten. Was sind die nächsten Risk-Off-Trigger? Konkrete Schwellen + Asset-Reaktionen für DXY, EUR/USD, BTC, NDX, SPX, DAX, Gold, US10Y, Öl.',
              {
                displayText: 'Japan-Carry-Lage analysieren',
              },
            )
          }
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[13px] font-bold text-amber-300 transition-colors hover:bg-amber-500/20"
        >
          <Sparkles className="size-4" />
          Frag EVE: aktuelle Carry-Lage + Risk-Off-Trigger
        </button>
      </div>
    </div>
  );
}

function KpiTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone: 'rose' | 'amber' | 'emerald' | 'neutral';
}) {
  const valueTone =
    tone === 'rose'
      ? 'text-rose-300'
      : tone === 'amber'
        ? 'text-amber-300'
        : tone === 'emerald'
          ? 'text-emerald-300'
          : 'text-neutral-100';
  const Icon =
    tone === 'rose' ? TrendingDown : tone === 'emerald' ? TrendingUp : null;
  return (
    <div className="glass rounded-2xl p-3">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`mt-1 flex items-center gap-1.5 text-base font-bold tabular ${valueTone}`}>
        {Icon ? <Icon className="size-4" /> : null}
        {value}
      </div>
      <div className="mt-1 text-[10px] text-neutral-500">{sub}</div>
    </div>
  );
}
